import type React from 'react';

import { useGetFace, useGetCard } from '@vlabs/api/artemy/client';
import type { FaceId } from '@vlabs/api/artemy/model/FaceId';
import { DetectionIcon } from '@vlabs/icons';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import { calcTagType } from '@vlabs/shared/utils';
import type { WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';

import { useTopMatchCardInfoTranslation } from './i18n';
import st from './TopMatchCardInfo.module.sass';
import { buildTestid } from '../../../shared/lib/utils';
import { EventFaceTopMatchSamplePoint3 } from '../detection-sample';

interface TopMatchCardInfoPoint3Props extends WithDataTestid {
  faceId: FaceId;
  similarity: number;
}

const displayName = 'TopMatchCardInfoPoint3';
const testid = buildTestid(displayName);

function TopMatchCardInfoPoint3({
  faceId,
  similarity,
  'data-testid': dataTestId = undefined,
}: TopMatchCardInfoPoint3Props): React.JSX.Element | null {
  const { t } = useTopMatchCardInfoTranslation();
  const { data: face } = useGetFace(faceId, { targets: ['card_id'] });
  const { data: card } = useGetCard(
    face?.card_id ?? '',
    { targets: ['user_data'] },
    { query: { enabled: !!face?.card_id } },
  );

  if (face && !face.card_id) return null;

  const kind = calcTagType(similarity);
  const fio = card?.user_data?.fio as string;

  return (
    <div
      className={cn(st.TopMatchCardInfo, {
        [st.TopMatchCardInfo_positive]: kind === 'positive',
        [st.TopMatchCardInfo_attention]: kind === 'attention',
        [st.TopMatchCardInfo_negative]: kind === 'negative',
      })}
      {...testid(dataTestId)}
    >
      <EventFaceTopMatchSamplePoint3
        className={st.Sample}
        faceId={faceId}
      />
      <div className={st.Info}>
        {!!similarity && (
          <SimilarityTag
            icon={<DetectionIcon />}
            size="large"
            title={t('tag')}
            value={similarity}
          />
        )}

        {!!fio && (
          <div className={st.Fio}>
            <span className={st.Title}>{t('fio')}</span>
            <span className={st.Value}>{fio}</span>
          </div>
        )}
      </div>
    </div>
  );
}

TopMatchCardInfoPoint3.displayName = displayName;

export { TopMatchCardInfoPoint3 };
